import AddIcon from '@mui/icons-material/Add';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { SimpleLinkField } from 'framework/ViewTypes';
import type { FlatListTableProps, ListTableAction, ListTableColumn } from 'framework/ui/ui-builder';
import { EMPTY_VALUE_INDICATOR } from 'framework/utils';
import { RECRUITMENT_POOL_STATUS, RECRUITMENT_POOL_STATUS_LABELS } from '../constants';
import type { RecruitmentPool, RecruitmentPoolStatus } from '../types';

type RecruitmentPoolTableActions = {
  canCreate: boolean;
  canUpdate: boolean;
  onCreate: () => void;
  onEdit: (pool: RecruitmentPool) => void;
  onTransition: (pool: RecruitmentPool, status: RecruitmentPoolStatus) => void;
};

const getColumns = (getDetailsUrl: (id: string) => string): ListTableColumn<RecruitmentPool>[] => [
  {
    id: 'name',
    label: 'Name',
    sortable: true,
    width: 170,
    render: pool => <SimpleLinkField to={getDetailsUrl(pool.id)} value={pool.name} />,
  },
  {
    id: 'status',
    label: 'Status',
    sortable: true,
    width: 120,
    render: pool => RECRUITMENT_POOL_STATUS_LABELS[pool.status],
  },
  // TODO Add related PFRs data once available
  {
    id: 'relatedPfrs',
    label: 'Related PFRs',
    width: 140,
    render: pool => EMPTY_VALUE_INDICATOR,
  },
  // TODO Add application in pipeline data once available
  {
    id: 'applicationInPipeline',
    label: 'Application in pipeline',
    width: 200,
    render: pool => EMPTY_VALUE_INDICATOR,
  },
  // TODO Add job publications data once available
  {
    id: 'jobPublications',
    label: 'Job publications',
    width: 160,
    render: pool => EMPTY_VALUE_INDICATOR,
  },
  // TODO Add health data once available
  {
    id: 'health',
    label: 'Health',
    width: 100,
    render: pool => EMPTY_VALUE_INDICATOR,
  },
];

const getActions = ({
  canUpdate,
  onEdit,
  onTransition,
}: RecruitmentPoolTableActions): ListTableAction<RecruitmentPool>[] => [
  {
    id: 'edit-recruitment-pool',
    label: 'Edit recruitment pool',
    icon: <EditOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Edit recruitment pool',
    isVisible: pool => canUpdate && pool.status !== RECRUITMENT_POOL_STATUS.CLOSED,
    onClick: onEdit,
  },
  {
    id: 'pause-recruitment-pool',
    label: 'Pause recruitment pool',
    icon: <PauseOutlinedIcon />,
    variant: 'outlined',
    tooltip: 'Pause recruitment pool',
    isVisible: pool => canUpdate && pool.status === RECRUITMENT_POOL_STATUS.ACTIVE,
    onClick: pool => onTransition(pool, RECRUITMENT_POOL_STATUS.PAUSED),
  },
  {
    id: 'activate-recruitment-pool',
    label: 'Activate recruitment pool',
    icon: <PlayArrowIcon />,
    variant: 'outlined',
    tooltip: 'Activate recruitment pool',
    isVisible: pool => canUpdate && pool.status === RECRUITMENT_POOL_STATUS.PAUSED,
    onClick: pool => onTransition(pool, RECRUITMENT_POOL_STATUS.ACTIVE),
  },
  {
    id: 'close-recruitment-pool',
    label: 'Close recruitment pool',
    icon: <DoDisturbIcon />,
    variant: 'outlined',
    color: 'error',
    tooltip: 'Close recruitment pool',
    isVisible: pool => canUpdate && pool.status === RECRUITMENT_POOL_STATUS.PAUSED,
    onClick: pool => onTransition(pool, RECRUITMENT_POOL_STATUS.CLOSED),
  },
];

export const getRecruitmentPoolsTableConfig = (
  rows: RecruitmentPool[],
  getDetailsUrl: (id: string) => string,
  actions: RecruitmentPoolTableActions,
): FlatListTableProps<RecruitmentPool> => ({
  rows,
  columns: getColumns(getDetailsUrl),
  rowActions: { actions: getActions(actions) },
  mainAction: actions.canCreate
    ? {
        id: 'create-recruitment-pool',
        label: 'Create recruitment pool',
        icon: <AddIcon />,
        variant: 'contained',
        tooltip: 'Create recruitment pool',
        onClick: actions.onCreate,
      }
    : undefined,
  emptyContent: 'No recruitment pools found.',
});
